
import OpportunityReportService from "@/services/api/opportunityReport";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import {
    createAsyncThunk,
    createSlice,
    isFulfilled,
    isRejected,
    isPending,
} from "@reduxjs/toolkit";

interface SalesReportState {
    opportunitiesByTeam: any[];
    opportunitiesByStatus: any[];
    teamPerformance: any;
    customerDetails: any;
    opportunityDetails: any;
    isFetching: boolean;
    teamListLoading: boolean;
    error: string | null;
}

const initialState: SalesReportState = {
    opportunitiesByTeam: [],
    opportunitiesByStatus: [],
    teamPerformance: null,
    customerDetails: null,
    opportunityDetails: null,
    isFetching: false,
    teamListLoading: false,
    error: null,
}

export const getOpportunityByTeam = createAsyncThunk(
    "report/opportunityByTeam",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await OpportunityReportService.getOpportunitiesByTeam(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to fetch Opportunities by Team Report");
        }
    }
)

export const exportOpportunitiesByTeam = createAsyncThunk(
    "report/exportOpportunityByTeam",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await OpportunityReportService.exportOpportunitiesByTeam(payload);
            return saveAsExcelFile(res, "OpportunitiesReport");
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to export Opportunities by Team Report");
        }
    }
)

export const getOpportunityByStatus = createAsyncThunk(
    "report/opportunityByStatus",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await OpportunityReportService.getOpportunityByStatus(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to fetch Opportunities by Status Report");
        }
    }
)

export const getTeamPerformance = createAsyncThunk(
    "report/teamPerformance",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await OpportunityReportService.getTeamPerformance(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to fetch Team Performance Report");
        }
    }
)

export const getCustomerDetails = createAsyncThunk(
    "report/customerDetails",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await OpportunityReportService.getCustomerDetails(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to fetch Customer Details");
        }
    }
)

export const getOpportunityDetails = createAsyncThunk(
    "report/opportunityDetails",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await OpportunityReportService.getOpportunityDetails(payload);
            return handleResponse(res, rejectWithValue);
        }
        catch (err: any) {
            rejectWithValue(err?.response?.data?.message || "Failed to fetch Opportunity Details");
        }
    }
)

const opportunityReportSlice = createSlice({
    name: "opportunityReport",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getOpportunityByTeam.fulfilled, (state, action) => {
                state.opportunitiesByTeam = action.payload || [];
            })
            .addCase(getOpportunityByStatus.fulfilled, (state, action) => {
                state.opportunitiesByStatus = action.payload || [];
            })
            .addCase(getCustomerDetails.fulfilled, (state, action) => {
                state.customerDetails = action.payload || null;
            })
            .addCase(getOpportunityDetails.fulfilled, (state, action) => {
                state.opportunityDetails = action.payload || null;
            })
            .addCase(getTeamPerformance.pending, (state) => {
                state.teamListLoading = true;
                state.teamPerformance = [];
            })
            .addCase(getTeamPerformance.fulfilled, (state, action) => {
                state.teamListLoading = false;
                state.teamPerformance = action.payload || null;
            })
            .addCase(getTeamPerformance.rejected, (state, action) => {
                state.teamListLoading = false;
                state.teamPerformance = action.payload || null;
            })
        builder
            .addMatcher(isPending, (state) => {
                state.isFetching = true;
                state.error = null;
            })
            .addMatcher(isFulfilled, (state) => {
                state.isFetching = false;
            })
            .addMatcher(isRejected, (state, action: any) => {
                state.isFetching = false;
                state.error = action.payload || "Something went wrong";
            });
    }
})

export default opportunityReportSlice.reducer;